import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Users, Calendar, CheckCircle, AlertCircle, Search, Building2, Award, X, Filter, Tag, Trash2, Bot, ChevronUp, Loader2 } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { categoryStore, Organization, CourseCategory, Level } from '../../store/categoryStore';
import { typeNames } from '../../constants/questionTypes';

interface Paper {
  id: string;
  title: string;
  organization: string;
  category: string;
  categoryType: string;
  level_text: string;
  year: string;
  month: string;
  paper_type: string;
  total_score: number;
  question_count: number;
}

interface StudentTagInfo {
  id: string;
  name: string;
  color: string;
}

interface Student {
  id: string;
  username: string;
  remark: string;
  tags: StudentTagInfo[]; // 多标签支持
}

interface StudentTag {
  id: string;
  name: string;
  color: string;
}

// 题型分布信息
interface QuestionTypeDistribution {
  type: string;
  count: number;
  totalScore: number;
}

const PAGE_SIZE = 10; // 每页加载数量

export default function AssignmentPublish() {
  const [papers, setPapers] = useState<Paper[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);

  // 分类数据
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [categories, setCategories] = useState<CourseCategory[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // 筛选状态
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // 选择状态
  const [selectedPapers, setSelectedPapers] = useState<Paper[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>('');
  const [assignmentType, setAssignmentType] = useState<'class' | 'competition'>('class');
  const [gradingMethod, setGradingMethod] = useState<'manual' | 'ai'>('manual');
  const [aiGradingEnabled, setAiGradingEnabled] = useState<boolean>(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // 标签相关状态
  const [availableTags, setAvailableTags] = useState<StudentTag[]>([]);
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');

  // 题型勾选状态：key 是 paperId，value 是该试卷启用的题型列表
  const [enabledQuestionTypes, setEnabledQuestionTypes] = useState<Record<string, string[]>>({});
  // 题型分布数据：key 是 paperId，value 是题型分布列表
  const [questionTypeDistributions, setQuestionTypeDistributions] = useState<Record<string, QuestionTypeDistribution[]>>({});
  const [loadingTypeDistributions, setLoadingTypeDistributions] = useState<Record<string, boolean>>({});

  // ========== 试卷懒加载状态 ==========
  const [papersPage, setPapersPage] = useState(1);
  const [papersHasMore, setPapersHasMore] = useState(true);
  const [papersLoadingMore, setPapersLoadingMore] = useState(false);
  const [allPapers, setAllPapers] = useState<Paper[]>([]); // 存储所有原始试卷数据
  const papersContainerRef = useRef<HTMLDivElement>(null);
  const papersObserverRef = useRef<IntersectionObserver | null>(null);
  const papersLoadMoreRef = useRef<HTMLDivElement>(null);

  // ========== 学员懒加载状态 ==========
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsHasMore, setStudentsHasMore] = useState(true);
  const [studentsLoadingMore, setStudentsLoadingMore] = useState(false);
  const [allStudents, setAllStudents] = useState<Student[]>([]); // 存储所有原始学员数据
  const studentsContainerRef = useRef<HTMLDivElement>(null);
  const studentsObserverRef = useRef<IntersectionObserver | null>(null);
  const studentsLoadMoreRef = useRef<HTMLDivElement>(null);

  // 回到顶部按钮显示状态
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    loadData();
    loadFilterData();
  }, []);

  const loadFilterData = async () => {
    setLoadingFilters(true);
    try {
      await categoryStore.init();
      setOrganizations(categoryStore.getOrganizations());
      setCategories(categoryStore.getCategories());
      setLevels(categoryStore.getLevels());
    } catch (err) {
      console.error('加载筛选数据失败:', err);
    } finally {
      setLoadingFilters(false);
    }
  };

  // 级联筛选计算
  const filteredCategories = useMemo(() => {
    if (!selectedOrganization) return [];
    return categories.filter(c => c.orgId === selectedOrganization);
  }, [categories, selectedOrganization]);

  const filteredLevels = useMemo(() => {
    if (!selectedCategory) return [];
    return levels.filter(l => l.catId === selectedCategory);
  }, [levels, selectedCategory]);

  // 级联筛选校验：上级改变时清空下级
  useEffect(() => {
    if (selectedOrganization && categories.length > 0) {
      const cats = categories.filter(c => c.orgId === selectedOrganization);
      if (cats.length > 0 && selectedCategory && !cats.find(c => c.id === selectedCategory)) {
        setSelectedCategory('');
        setSelectedLevel('');
      }
    }
  }, [selectedOrganization, categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory && levels.length > 0) {
      const levs = levels.filter(l => l.catId === selectedCategory);
      if (levs.length > 0 && selectedLevel && !levs.find(l => l.id === selectedLevel)) {
        setSelectedLevel('');
      }
    }
  }, [selectedCategory, levels, selectedLevel]);

  // 加载试卷题型分布
  const loadQuestionTypeDistribution = async (paperId: string) => {
    if (questionTypeDistributions[paperId]) return; // 已加载过
    setLoadingTypeDistributions(prev => ({ ...prev, [paperId]: true }));
    try {
      const { data: questionsData } = await supabase
        .from('questions')
        .select('question_type, score')
        .eq('paper_id', paperId);

      if (questionsData) {
        // 统计题型分布
        const distribution: QuestionTypeDistribution[] = [];
        const typeMap: Record<string, { count: number; totalScore: number }> = {};
        questionsData.forEach((q: any) => {
          const type = q.question_type || 'single_choice';
          if (!typeMap[type]) {
            typeMap[type] = { count: 0, totalScore: 0 };
          }
          typeMap[type].count++;
          typeMap[type].totalScore += q.score || 2;
        });

        // 按题型顺序排列：客观题在前，编程题在后
        const orderedTypes = ['single_choice', 'multi_choice', 'judge', 'scratch_coding', 'python_coding', 'cpp_coding'];
        const otherTypes = Object.keys(typeMap).filter(t => !orderedTypes.includes(t));
        const allTypes = [...orderedTypes.filter(t => typeMap[t]), ...otherTypes.filter(t => typeMap[t])];

        allTypes.forEach(type => {
          distribution.push({
            type,
            count: typeMap[type].count,
            totalScore: typeMap[type].totalScore
          });
        });

        setQuestionTypeDistributions(prev => ({ ...prev, [paperId]: distribution }));
        // 默认全部勾选
        setEnabledQuestionTypes(prev => ({ ...prev, [paperId]: distribution.map(d => d.type) }));
      }
    } catch (err) {
      console.error('加载题型分布失败:', err);
    }
    setLoadingTypeDistributions(prev => ({ ...prev, [paperId]: false }));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      // 获取可用试卷
      const papersResponse = await fetch(`${getSupabaseUrl()}/functions/v1/teacher-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'get_available_papers_for_assignment' }),
      });
      const papersResult = await papersResponse.json();
      if (papersResult.success) {
        setAllPapers(papersResult.data);
        // 初始加载前10条
        setPapers(papersResult.data.slice(0, PAGE_SIZE));
        setPapersHasMore(papersResult.data.length > PAGE_SIZE);
      }

      // 获取已绑定学员和标签
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // 加载AI阅卷开关状态
        const { data: examSettings } = await supabase.from('exam_settings').select('ai_grading_enabled').single();
        if (examSettings) {
          setAiGradingEnabled(examSettings.ai_grading_enabled ?? false);
        }
        // 加载标签
        const { data: tagsData } = await supabase
          .from('teacher_student_tags')
          .select('*')
          .eq('teacher_id', user.id)
          .order('sort_order', { ascending: true });
        setAvailableTags(tagsData || []);

        // 加载学员
        const { data: teacherStudents } = await supabase
          .from('teacher_students')
          .select('student_id, remark')
          .eq('teacher_id', user.id);

        if (teacherStudents && teacherStudents.length > 0) {
          const studentIds = teacherStudents.map(s => s.student_id);
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, username')
            .in('id', studentIds);

          // 加载学员标签关联
          const { data: tagRelations } = await supabase
            .from('teacher_student_tag_relations')
            .select('student_id, tag_id')
            .eq('teacher_id', user.id)
            .in('student_id', studentIds);

          const studentList = (profiles || []).map(profile => {
            const teacherStudent = teacherStudents.find(s => s.student_id === profile.id);
            // 获取该学员的所有标签
            const studentTagIds = tagRelations
              ?.filter(r => r.student_id === profile.id)
              .map(r => r.tag_id) || [];
            const studentTags = tagsData
              ?.filter(t => studentTagIds.includes(t.id))
              .map(t => ({ id: t.id, name: t.name, color: t.color })) || [];

            return {
              id: profile.id,
              username: profile.username,
              remark: teacherStudent?.remark || '',
              tags: studentTags,
            };
          });
          setAllStudents(studentList);
          // 初始加载前10条
          setStudents(studentList.slice(0, PAGE_SIZE));
          setStudentsHasMore(studentList.length > PAGE_SIZE);
        }
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    }
    setLoading(false);
  };

  // ========== 试卷筛选（基于所有数据） ==========
  const filteredAllPapers = useMemo(() => {
    return allPapers.filter(paper => {
      if (selectedOrganization) {
        const org = organizations.find(o => o.id === selectedOrganization);
        if (!org || paper.organization !== org.name) return false;
      }
      if (selectedCategory) {
        const cat = categories.find(c => c.id === selectedCategory);
        if (!cat || paper.category !== cat.name) return false;
      }
      if (selectedLevel) {
        const level = levels.find(l => l.id === selectedLevel);
        if (!level || paper.level_text !== level.name) return false;
      }
      if (searchQuery.trim()) {
        const searchTerms = searchQuery.trim().toLowerCase().split(/\s+/).filter(term => term.length > 0);
        if (searchTerms.length === 0) return true;
        const title = (paper.title || '').toLowerCase();
        return searchTerms.every(term => title.includes(term));
      }
      return true;
    });
  }, [allPapers, selectedOrganization, selectedCategory, selectedLevel, searchQuery, organizations, categories, levels]);

  // 筛选条件变化时重置试卷分页
  useEffect(() => {
    setPapersPage(1);
    setPapers(filteredAllPapers.slice(0, PAGE_SIZE));
    setPapersHasMore(filteredAllPapers.length > PAGE_SIZE);
  }, [filteredAllPapers]);

  // 加载更多试卷
  const loadMorePapers = useCallback(() => {
    if (papersLoadingMore || !papersHasMore) return;
    
    setPapersLoadingMore(true);
    const nextPage = papersPage + 1;
    const startIndex = (nextPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const newPapers = filteredAllPapers.slice(startIndex, endIndex);
    
    setTimeout(() => {
      setPapers(prev => [...prev, ...newPapers]);
      setPapersPage(nextPage);
      setPapersHasMore(endIndex < filteredAllPapers.length);
      setPapersLoadingMore(false);
    }, 300); // 添加小延迟以显示加载效果
  }, [papersPage, papersHasMore, papersLoadingMore, filteredAllPapers]);

  // 试卷滚动监听
  useEffect(() => {
    if (papersObserverRef.current) {
      papersObserverRef.current.disconnect();
    }

    papersObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && papersHasMore && !papersLoadingMore) {
          loadMorePapers();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (papersLoadMoreRef.current) {
      papersObserverRef.current.observe(papersLoadMoreRef.current);
    }

    return () => {
      if (papersObserverRef.current) {
        papersObserverRef.current.disconnect();
      }
    };
  }, [loadMorePapers, papersHasMore, papersLoadingMore]);

  // ========== 学员筛选（基于所有数据） ==========
  const filteredAllStudents = useMemo(() => {
    return allStudents.filter(student => {
      // 标签筛选 - OR 逻辑：只要学员拥有任一选中标签即匹配
      if (selectedTagFilters.length > 0) {
        const hasMatchingTag = student.tags.some(tag => selectedTagFilters.includes(tag.id));
        if (!hasMatchingTag) {
          return false;
        }
      }
      // 搜索筛选
      if (studentSearchQuery.trim()) {
        const query = studentSearchQuery.trim().toLowerCase();
        const username = (student.username || '').toLowerCase();
        const remark = (student.remark || '').toLowerCase();
        const tagNames = student.tags.map(t => t.name.toLowerCase()).join(' ');
        return username.includes(query) || remark.includes(query) || tagNames.includes(query);
      }
      return true;
    });
  }, [allStudents, selectedTagFilters, studentSearchQuery]);

  // 筛选条件变化时重置学员分页
  useEffect(() => {
    setStudentsPage(1);
    setStudents(filteredAllStudents.slice(0, PAGE_SIZE));
    setStudentsHasMore(filteredAllStudents.length > PAGE_SIZE);
  }, [filteredAllStudents]);

  // 加载更多学员
  const loadMoreStudents = useCallback(() => {
    if (studentsLoadingMore || !studentsHasMore) return;
    
    setStudentsLoadingMore(true);
    const nextPage = studentsPage + 1;
    const startIndex = (nextPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const newStudents = filteredAllStudents.slice(startIndex, endIndex);
    
    setTimeout(() => {
      setStudents(prev => [...prev, ...newStudents]);
      setStudentsPage(nextPage);
      setStudentsHasMore(endIndex < filteredAllStudents.length);
      setStudentsLoadingMore(false);
    }, 300);
  }, [studentsPage, studentsHasMore, studentsLoadingMore, filteredAllStudents]);

  // 学员滚动监听
  useEffect(() => {
    if (studentsObserverRef.current) {
      studentsObserverRef.current.disconnect();
    }

    studentsObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && studentsHasMore && !studentsLoadingMore) {
          loadMoreStudents();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (studentsLoadMoreRef.current) {
      studentsObserverRef.current.observe(studentsLoadMoreRef.current);
    }

    return () => {
      if (studentsObserverRef.current) {
        studentsObserverRef.current.disconnect();
      }
    };
  }, [loadMoreStudents, studentsHasMore, studentsLoadingMore]);

  // 获取已选学员的完整信息
  const selectedStudentDetails = useMemo(() => {
    return allStudents.filter(s => selectedStudents.includes(s.id));
  }, [allStudents, selectedStudents]);

  // 回到顶部功能
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 监听滚动显示回到顶部按钮
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const clearFilters = () => {
    setSelectedOrganization('');
    setSelectedCategory('');
    setSelectedLevel('');
    setSearchQuery('');
  };

  const handlePublish = async () => {
    if (selectedPapers.length === 0) {
      setMessage({ type: 'error', text: '请至少选择一份试卷' });
      return;
    }
    if (selectedStudents.length === 0) {
      setMessage({ type: 'error', text: '请至少选择一名学员' });
      return;
    }

    // 检查教师是否有有效刷题包
    setPublishing(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) {
        setMessage({ type: 'error', text: '请先登录' });
        setPublishing(false);
        return;
      }

      const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
      const { data: teacherPackages } = await supabase
        .from('teacher_packages')
        .select('*')
        .eq('teacher_id', session.user.id)
        .eq('is_active', true)
        .gte('expire_date', today);

      if (!teacherPackages || teacherPackages.length === 0) {
        setMessage({ type: 'error', text: '请先购买刷题包才能发布作业' });
        setPublishing(false);
        return;
      }

      const results = [];
      const errors = [];

      for (const paper of selectedPapers) {
        const paperEnabledTypes = enabledQuestionTypes[paper.id];
        const distribution = questionTypeDistributions[paper.id];
        // 如果全部题型都勾选了，传 null（表示全部启用，向后兼容）
        const allEnabled = distribution && paperEnabledTypes && paperEnabledTypes.length === distribution.length;
        const typesToSend = allEnabled ? null : paperEnabledTypes;

        const response = await fetch(`${getSupabaseUrl()}/functions/v1/teacher-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            action: 'create_assignment',
            paperId: paper.id,
            paperTitle: paper.title,
            studentIds: selectedStudents,
            deadline: deadline || null,
            enabledQuestionTypes: typesToSend,
            assignmentType,
            gradingMethod,
          }),
        });

        const result = await response.json();
        if (result.success) {
          results.push(paper.title);
        } else {
          errors.push(`${paper.title}: ${result.message}`);
        }
      }

      if (errors.length === 0) {
        setMessage({ type: 'success', text: `成功发布 ${results.length} 份作业` });
        setSelectedPapers([]);
        setSelectedStudents([]);
        setSelectedTagFilters([]);
        setStudentSearchQuery('');
        setDeadline('');
        setEnabledQuestionTypes({});
        setQuestionTypeDistributions({});
      } else if (results.length > 0) {
        setMessage({ type: 'warning', text: `成功发布 ${results.length} 份，失败 ${errors.length} 份` });
        setSelectedPapers([]);
        setSelectedStudents([]);
        setSelectedTagFilters([]);
        setStudentSearchQuery('');
        setDeadline('');
        setEnabledQuestionTypes({});
        setQuestionTypeDistributions({});
      } else {
        setMessage({ type: 'error', text: errors[0] });
      }
    } catch (error) {
      console.error('发布作业失败:', error);
      setMessage({ type: 'error', text: '发布作业失败，请稍后重试' });
    }
    setPublishing(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const removeSelectedStudent = (studentId: string) => {
    setSelectedStudents(prev => prev.filter(id => id !== studentId));
  };

  const clearAllSelectedStudents = () => {
    setSelectedStudents([]);
    setSelectedTagFilters([]);
  };

  const toggleTagFilter = (tagId: string) => {
    setSelectedTagFilters(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const selectAllStudents = () => {
    if (selectedStudents.length === filteredAllStudents.length) {
      // 取消选择当前筛选结果中的学员
      const filteredIds = new Set(filteredAllStudents.map(s => s.id));
      setSelectedStudents(prev => prev.filter(id => !filteredIds.has(id)));
    } else {
      // 选择当前筛选结果中的所有学员（避免重复）
      const filteredIds = filteredAllStudents.map(s => s.id);
      setSelectedStudents(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  // 试卷选择相关函数
  const togglePaperSelection = (paper: Paper) => {
    setSelectedPapers(prev => {
      const isCurrentlySelected = prev.find(p => p.id === paper.id) !== undefined;
      if (isCurrentlySelected) {
        // 取消选中时，清除该试卷的题型数据
        setEnabledQuestionTypes(prevTypes => {
          const newTypes = { ...prevTypes };
          delete newTypes[paper.id];
          return newTypes;
        });
        setQuestionTypeDistributions(prevDist => {
          const newDist = { ...prevDist };
          delete newDist[paper.id];
          return newDist;
        });
        return prev.filter(p => p.id !== paper.id);
      } else {
        // 选中时，加载题型分布
        loadQuestionTypeDistribution(paper.id);
        return [...prev, paper];
      }
    });
  };

  const selectAllPapers = () => {
    if (selectedPapers.length === filteredAllPapers.length) {
      setSelectedPapers([]);
      setEnabledQuestionTypes({});
      setQuestionTypeDistributions({});
    } else {
      setSelectedPapers([...filteredAllPapers]);
      // 加载所有选中试卷的题型分布
      filteredAllPapers.forEach(paper => {
        loadQuestionTypeDistribution(paper.id);
      });
    }
  };

  // 题型勾选切换
  const toggleQuestionType = (paperId: string, type: string) => {
    setEnabledQuestionTypes(prev => {
      const currentTypes = prev[paperId] || [];
      const distribution = questionTypeDistributions[paperId] || [];
      if (currentTypes.includes(type)) {
        // 至少保留一个题型
        if (currentTypes.length <= 1) return prev;
        return { ...prev, [paperId]: currentTypes.filter(t => t !== type) };
      } else {
        return { ...prev, [paperId]: [...currentTypes, type] };
      }
    });
  };

  // 计算勾选后的有效总分
  const getEffectiveTotalScore = (paperId: string) => {
    const distribution = questionTypeDistributions[paperId];
    const enabledTypes = enabledQuestionTypes[paperId];
    if (!distribution || !enabledTypes) return 0;
    return distribution
      .filter(d => enabledTypes.includes(d.type))
      .reduce((sum, d) => sum + d.totalScore, 0);
  };

  // 计算勾选后的有效题数
  const getEffectiveQuestionCount = (paperId: string) => {
    const distribution = questionTypeDistributions[paperId];
    const enabledTypes = enabledQuestionTypes[paperId];
    if (!distribution || !enabledTypes) return 0;
    return distribution
      .filter(d => enabledTypes.includes(d.type))
      .reduce((sum, d) => sum + d.count, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">发布作业</h1>
      </div>

      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
          }`}
        >
          {message.text}
        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 左侧：试卷选择 */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-800">选择试卷</h2>
            {selectedPapers.length > 0 && (
              <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded-full">
                已选 {selectedPapers.length} 份
              </span>
            )}
          </div>

          {/* 三级级联筛选 */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-4">
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">考评体系</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {loadingFilters ? (
                    <div className="px-4 py-2 text-sm text-gray-400">加载中...</div>
                  ) : organizations.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-400">暂无考评体系</div>
                  ) : (
                    organizations.map(org => (
                      <motion.button
                        key={org.id}
                        onClick={() => { setSelectedOrganization(org.id); setSelectedCategory(''); setSelectedLevel(''); }}
                        className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                          selectedOrganization === org.id
                            ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {org.name}
                      </motion.button>
                    ))
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">课程分类</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!selectedOrganization ? (
                    <div className="px-4 py-2 text-sm text-gray-400">请先选择考评体系</div>
                  ) : filteredCategories.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-400">暂无课程分类</div>
                  ) : (
                    filteredCategories.map(cat => (
                      <motion.button
                        key={cat.id}
                        onClick={() => { setSelectedCategory(cat.id); setSelectedLevel(''); }}
                        className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                          selectedCategory === cat.id
                            ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {cat.name}
                      </motion.button>
                    ))
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">级别</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!selectedCategory ? (
                    <div className="px-4 py-2 text-sm text-gray-400">请先选择课程分类</div>
                  ) : filteredLevels.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-400">暂无级别</div>
                  ) : (
                    filteredLevels.map(level => (
                      <motion.button
                        key={level.id}
                        onClick={() => setSelectedLevel(level.id)}
                        className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                          selectedLevel === level.id
                            ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {level.name}
                      </motion.button>
                    ))
                  )}
                </div>
              </div>

              {(selectedOrganization || selectedCategory || selectedLevel) && (
                <div className="pt-2 border-t border-gray-200">
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                    清空筛选条件
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 搜索框 */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索试卷标题（支持空格分隔多条件）"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            />
          </div>

          {/* 试卷数量统计和全选 */}
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {searchQuery.trim() ? (
                filteredAllPapers.length === 0 ? (
                  <span>未找到匹配的试卷</span>
                ) : (
                  <span>共找到 <span className="font-medium text-purple-600">{filteredAllPapers.length}</span> 份试卷</span>
                )
              ) : (
                <span>共 <span className="font-medium text-gray-900">{filteredAllPapers.length}</span> 份试卷</span>
              )}
            </div>
            {filteredAllPapers.length > 0 && (
              <button
                onClick={selectAllPapers}
                className="text-sm text-purple-600 hover:text-purple-700"
              >
                {selectedPapers.length === filteredAllPapers.length ? '取消全选' : '全选'}
              </button>
            )}
          </div>

          {/* 试卷列表 - 懒加载 */}
          <div 
            ref={papersContainerRef}
            className="space-y-2 max-h-[400px] overflow-y-auto"
          >
            {papers.length > 0 ? (
              <>
                {papers.map(paper => {
                  const isSelected = selectedPapers.find(p => p.id === paper.id) !== undefined;
                  return (
                    <div
                      key={paper.id}
                      onClick={() => togglePaperSelection(paper)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-800 text-sm">{paper.title}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {paper.organization} · {paper.category} · {paper.level_text}
                            {paper.year && ` · ${paper.year}年`}
                            {paper.month && `${paper.month}月`}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {paper.question_count}题 · 满分{paper.total_score}分
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* 加载更多指示器 */}
                <div ref={papersLoadMoreRef} className="py-3 text-center">
                  {papersLoadingMore ? (
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">加载中...</span>
                    </div>
                  ) : papersHasMore ? (
                    <span className="text-xs text-gray-400">上拉加载更多</span>
                  ) : papers.length >= PAGE_SIZE ? (
                    <span className="text-xs text-gray-400">没有更多试卷了</span>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <p>{searchQuery.trim() ? '未找到匹配的试卷' : '暂无符合条件的试卷'}</p>
                {searchQuery.trim() && (
                  <p className="text-sm text-gray-400 mt-2">尝试使用其他关键词或空格分隔多个条件</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 右侧：题型勾选 + 学员选择 */}
        <div className="space-y-6">
          {/* 题型勾选面板 */}
          {selectedPapers.length > 0 && (
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-800">题型勾选</h2>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                取消勾选的题型，学生答题时将不显示，也不计入总分。每个题型至少保留一项。
              </p>

              <div className="space-y-4">
                {selectedPapers.map(paper => {
                  const distribution = questionTypeDistributions[paper.id];
                  const enabledTypes = enabledQuestionTypes[paper.id] || [];
                  const isLoading = loadingTypeDistributions[paper.id];
                  const effectiveTotalScore = getEffectiveTotalScore(paper.id);
                  const effectiveQuestionCount = getEffectiveQuestionCount(paper.id);
                  const isAllEnabled = distribution && enabledTypes.length === distribution.length;

                  return (
                    <div key={paper.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-800 text-sm">{paper.title}</h3>
                        {!isAllEnabled && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-600 text-xs rounded-full">
                            已筛选题型
                          </span>
                        )}
                      </div>

                      {isLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                          <span className="ml-2 text-sm text-gray-400">加载题型...</span>
                        </div>
                      ) : distribution ? (
                        <>
                          <div className="space-y-2">
                            {distribution.map(dist => {
                              const isEnabled = enabledTypes.includes(dist.type);
                              return (
                                <div
                                  key={dist.type}
                                  className={`flex items-center justify-between p-2 rounded-lg transition-all ${
                                    isEnabled ? 'bg-purple-50' : 'bg-gray-100 opacity-60'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleQuestionType(paper.id, dist.type); }}
                                      className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                                        isEnabled
                                          ? 'bg-purple-600 border-purple-600'
                                          : 'border-gray-300 hover:border-purple-400'
                                      }`}
                                    >
                                      {isEnabled && <CheckCircle className="w-3 h-3 text-white" />}
                                    </button>
                                    <span className={`text-sm ${isEnabled ? 'text-gray-800' : 'text-gray-500'}`}>
                                      {typeNames[dist.type] || dist.type}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs">
                                    <span className={`text-gray-500`}>{dist.count}题</span>
                                    <span className={`font-medium ${isEnabled ? 'text-purple-600' : 'text-gray-400'}`}>
                                      {dist.totalScore}分
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {!isAllEnabled && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600">有效题数/总分：</span>
                                <span className="font-medium text-purple-600">
                                  {effectiveQuestionCount}题 / {effectiveTotalScore}分
                                </span>
                              </div>
                              <p className="text-xs text-amber-600 mt-1">
                                原试卷 {paper.question_count}题/{paper.total_score}分，筛选后 {effectiveQuestionCount}题/{effectiveTotalScore}分
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-gray-400 py-2">题型数据加载中...</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 学员选择 */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-800">选择学员</h2>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">
                  已选 {selectedStudents.length} 人
                </span>
              </div>
            </div>

            {/* 标签筛选区域 */}
            {availableTags.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">按标签筛选（多选，OR逻辑）</span>
                  {selectedTagFilters.length > 0 && (
                    <button
                      onClick={() => setSelectedTagFilters([])}
                      className="text-xs text-gray-500 hover:text-red-600 ml-2"
                    >
                      清空筛选
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map(tag => {
                    const isSelected = selectedTagFilters.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        onClick={() => toggleTagFilter(tag.id)}
                        className={`px-3 py-1.5 text-sm rounded-full border-2 transition-all ${
                          isSelected
                            ? 'border-transparent text-white'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 bg-white'
                        }`}
                        style={isSelected ? { backgroundColor: tag.color, borderColor: tag.color } : {}}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 学员搜索 */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜索学员姓名、备注或标签"
                value={studentSearchQuery}
                onChange={(e) => setStudentSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>

            {/* 学员列表操作栏 */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-gray-600">
                {filteredAllStudents.length === 0 ? (
                  <span>未找到匹配的学员</span>
                ) : (
                  <span>共 <span className="font-medium text-gray-900">{filteredAllStudents.length}</span> 位学员</span>
                )}
              </div>
              {filteredAllStudents.length > 0 && (
                <button
                  onClick={selectAllStudents}
                  className="text-sm text-purple-600 hover:text-purple-700"
                >
                  {filteredAllStudents.every(s => selectedStudents.includes(s.id)) ? '取消全选' : '全选'}
                </button>
              )}
            </div>

            {/* 学员列表 - 懒加载 */}
            {allStudents.length > 0 ? (
              <div 
                ref={studentsContainerRef}
                className="space-y-2 max-h-[250px] overflow-y-auto mb-4"
              >
                {students.map(student => {
                  const isSelected = selectedStudents.includes(student.id);
                  return (
                    <div
                      key={student.id}
                      onClick={() => toggleStudentSelection(student.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                        isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                      }`}>
                        {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-800 text-sm">{student.username}</p>
                          {/* 显示多个标签 */}
                          {student.tags.length > 0 && (
                            <div className="flex items-center gap-1">
                              {student.tags.map(tag => (
                                <span
                                  key={tag.id}
                                  className="px-2 py-0.5 rounded-full text-xs text-white"
                                  style={{ backgroundColor: tag.color || '#6366f1' }}
                                >
                                  {tag.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {student.remark && <p className="text-xs text-gray-500">{student.remark}</p>}
                      </div>
                    </div>
                  );
                })}
                
                {/* 加载更多指示器 */}
                <div ref={studentsLoadMoreRef} className="py-3 text-center">
                  {studentsLoadingMore ? (
                    <div className="flex items-center justify-center gap-2 text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">加载中...</span>
                    </div>
                  ) : studentsHasMore ? (
                    <span className="text-xs text-gray-400">上拉加载更多</span>
                  ) : students.length >= PAGE_SIZE ? (
                    <span className="text-xs text-gray-400">没有更多学员了</span>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400 mb-4">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>暂无绑定学员</p>
              </div>
            )}

            {/* 已选学员区域 */}
            <AnimatePresence>
              {selectedStudentDetails.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-t border-gray-200 pt-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">已选择学员</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs rounded-full">
                        {selectedStudentDetails.length} 人
                      </span>
                    </div>
                    <button
                      onClick={clearAllSelectedStudents}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      清空全部
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto">
                    {selectedStudentDetails.map(student => (
                      <div
                        key={student.id}
                        className="flex items-center gap-1 px-2 py-1 bg-purple-50 border border-purple-200 rounded-lg text-sm"
                      >
                        <span className="text-gray-800">{student.username}</span>
                        {/* 显示多个标签 */}
                        {student.tags.length > 0 && (
                          <div className="flex items-center gap-0.5">
                            {student.tags.map(tag => (
                              <span
                                key={tag.id}
                                className="px-1 py-0.5 rounded text-xs text-white"
                                style={{ backgroundColor: tag.color || '#6366f1' }}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); removeSelectedStudent(student.id); }}
                          className="ml-1 p-0.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 作业类型选择 */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-purple-600" />
                <h3 className="font-medium text-gray-800">作业类型 <span className="text-red-500">*</span></h3>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="assignmentType"
                    value="class"
                    checked={assignmentType === 'class'}
                    onChange={(e) => setAssignmentType(e.target.value as 'class' | 'competition')}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">课堂作业</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="assignmentType"
                    value="competition"
                    checked={assignmentType === 'competition'}
                    onChange={(e) => setAssignmentType(e.target.value as 'class' | 'competition')}
                    className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">考级竞赛</span>
                </label>
              </div>
            </div>

            {/* 编程题评卷方式 */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Bot className="w-5 h-5 text-indigo-600" />
                <h3 className="font-medium text-gray-800">编程题评卷方式</h3>
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="gradingMethod"
                    value="manual"
                    checked={gradingMethod === 'manual'}
                    onChange={(e) => setGradingMethod(e.target.value as 'manual' | 'ai')}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">人工阅卷</span>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">教师手动评分</span>
                </label>
                <label className={`flex items-center gap-2 ${!aiGradingEnabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                  <input
                    type="radio"
                    name="gradingMethod"
                    value="ai"
                    checked={gradingMethod === 'ai'}
                    onChange={(e) => { if (aiGradingEnabled) setGradingMethod(e.target.value as 'manual' | 'ai'); }}
                    disabled={!aiGradingEnabled}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                  />
                  <span className="text-sm text-gray-700">AI阅卷</span>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium">AI自动评分</span>
                  {!aiGradingEnabled && (
                    <span className="text-xs text-gray-400">(维护中)</span>
                  )}
                </label>
              </div>
              {!aiGradingEnabled && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  AI阅卷功能当前未开启，仅支持人工阅卷模式
                </p>
              )}
            </div>

            {/* 截止时间设置 */}
            <div className="border-t border-gray-100 pt-4 mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5 text-purple-600" />
                <h3 className="font-medium text-gray-800">截止时间（可选）</h3>
              </div>
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">不设置则作业长期有效</p>
            </div>
          </div>
        </div>
      </div>

      {/* 发布按钮 */}
      <div className="flex justify-end">
        <button
          onClick={handlePublish}
          disabled={publishing || selectedPapers.length === 0 || selectedStudents.length === 0}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
        >
          {publishing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              发布中...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5" />
              发布作业 ({selectedPapers.length}份试卷 × {selectedStudents.length}人)
            </>
          )}
        </button>
      </div>

      {/* 回到顶部按钮 */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 w-12 h-12 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-colors flex items-center justify-center z-50"
          >
            <ChevronUp className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
